import React, { useState, useEffect } from "react";
import {Container, Modal, Button, Badge, Card, Table, Row, Col, Spinner, Alert } from "react-bootstrap";
import { Link } from "react-router-dom";
import axios from "axios";
import BASE_URL from "../config";
import './SalesHistory.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { FaUserTie, FaUser, FaPhoneAlt, FaBox, FaCheckCircle, FaEye, FaMapMarkerAlt } from "react-icons/fa";

// --- START OF SHARED UTILITY FUNCTIONS ---

/**
 * Helper function to clean the BASE_URL to the domain root for serving static files.
 * This resolves the common 404 issue when assets are served outside the /api route.
 */
const getStorageBaseUrl = () => {
    try {
        const url = new URL(BASE_URL);
        
        let root = `${url.protocol}//${url.hostname}`;
        
        if (url.port && url.port !== '80' && url.port !== '443') {
            root += `:${url.port}`;
        }
        
        if (root.endsWith('/')) {
            root = root.slice(0, -1);
        }

        return root;
    } catch (e) {
        console.error("Error parsing BASE_URL for static files:", e);
        return BASE_URL.replace(/\/api$/, '');
    }
};

// --- END OF SHARED UTILITY FUNCTIONS ---


// Axios instance with interceptor for authentication
const authAxios = axios.create({ baseURL: BASE_URL });
authAxios.interceptors.request.use((config) => {
    const token = localStorage.getItem("authToken");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

const SalesHistory = () => {
    const [activeForm, setActiveForm] = useState(null);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [trackingData, setTrackingData] = useState(null);
    const [trackingLoading, setTrackingLoading] = useState(false);
    const [showProofModal, setShowProofModal] = useState(false);
    const [selectedProof, setSelectedProof] = useState(null);

    // Fetch orders history from API
    const fetchOrdersHistory = async () => {
        setLoading(true);
        setError(null);
        try {
            // NOTE: Using general /orders endpoint might be safer if /orders/history is not standard
            const response = await authAxios.get('/orders?embed=customer,vendor,driver,attachments');
            
            // Function to generate corrected attachment URL
            const getAttachmentUrl = (path) => {
                const storageRoot = getStorageBaseUrl();
                const publicStoragePath = path.startsWith('storage/') ? path : `storage/${path}`;
                return `${storageRoot}/${publicStoragePath}`;
            };
            
            const fetchedOrders = response.data.data.data || [];

            // Pre-process attachments for correct URLs (FIXED)
            const processedOrders = fetchedOrders.map(order => {
                const attachments = Array.isArray(order.attachments?.data ? order.attachments.data : order.attachments)
                    ? (order.attachments?.data || order.attachments)
                    : [];

                const mapAttachments = (type) => attachments
                    .filter(a => a.type?.toLowerCase().includes(type.toLowerCase()))
                    .map(a => ({ ...a, url: a.path ? getAttachmentUrl(a.path) : null }));

                return {
                    ...order,
                    attachments: {
                        pod_photos: mapAttachments('pod'),
                        voice_notes: mapAttachments('voice'),
                        order_docs: mapAttachments('document')
                    }
                };
            });

            setOrders(processedOrders);
        } catch (err) {
            console.error('Error fetching orders history:', err);
            setError('Failed to fetch orders history. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    // Fetch tracking details for a specific order
    const fetchOrderTracking = async (orderId) => {
        setTrackingLoading(true);
        try {
            const response = await authAxios.get(`/orders/${orderId}/tracking?embed=attachments`);
            
            const trackingDataRaw = response.data.data || null;

            if (trackingDataRaw && trackingDataRaw.attachments) {
                 const attachments = trackingDataRaw.attachments;

                 const getAttachmentUrl = (path) => {
                    const storageRoot = getStorageBaseUrl();
                    const publicStoragePath = path.startsWith('storage/') ? path : `storage/${path}`;
                    return `${storageRoot}/${publicStoragePath}`;
                 };

                 // Manually ensure attachment URLs are correct (FIXED)
                 const correctedAttachments = attachments.map(att => ({
                    ...att,
                    url: att.path ? getAttachmentUrl(att.path) : att.url
                 }));

                 trackingDataRaw.attachments = correctedAttachments;
            }

            setTrackingData(trackingDataRaw);
        } catch (err) {
            console.error('Error fetching tracking data:', err);
            setTrackingData(null);
        } finally {
            setTrackingLoading(false);
        }
    };

    // Handle order selection
    const handleOrderSelect = (order) => {
        setSelectedOrder(order);
        fetchOrderTracking(order.id);
    };

    // Handle proof image view (FIXED URL construction)
    const handleViewProof = (proofType) => {
        if (trackingData && trackingData.attachments) {
            
            // Filter to find the first proof matching the type
            const proof = trackingData.attachments.find(att => 
                att.type.toLowerCase().includes(proofType.toLowerCase())
            );

            if (proof) {
                // The URL should already be corrected from fetchOrderTracking, use it directly
                setSelectedProof(proof);
                setShowProofModal(true);
            } else {
                alert(`No ${proofType} proof available for this order.`);
            }
        }
    };

    // Initialize component
    useEffect(() => {
        fetchOrdersHistory();
    }, []);

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric' 
        }).replace(/\//g, '-'); // Use dd-mm-yyyy
    };

    // Format time for display
    const formatTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        });
    };

    // Get status color for timeline
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
            case 'delivered':
            case 'returned':
                return 'bg-success';
            case 'pending':
            case 'assigned':
                return 'bg-warning';
            case 'in_transit':
            case 'on the way':
                return 'bg-primary';
            default:
                return 'bg-secondary';
        }
    };

    // Get status text
    const getStatusText = (status) => {
        switch (status?.toLowerCase()) {
            case 'pending':
                return 'Order Placed';
            case 'assigned':
                return 'Driver Assigned';
            case 'in_transit':
                return 'On the Way';
            case 'picked_up':
                return 'To Pickup';
            case 'delivered':
                return 'Completed';
            case 'returned':
                return 'Returned';
            default:
                return status || 'Unknown';
        }
    };

    return (
        <div style={{background:"rgb(0, 24, 127)"}}>
            <div className="container justify-content-center align-items-center vh-100">
                <div className="row">
                    <div className="col-md-10 col-lg-10" style={{ margin: "20px auto" }}>
                        <Container fluid className="p-4 sales-history-page for_background">
                            {/* Header */}
                            <Row className="mb-4">
                                <Col className="d-flex justify-content-between align-items-center">
                                    <h5 className="fw-bold">
                                        Order #{selectedOrder ? (selectedOrder.po_number || selectedOrder.id) : 'Select an Order'}
                                    </h5>
                                    {selectedOrder && (
                                        <Badge bg={getStatusColor(selectedOrder.status)} className="shadow-sm">
                                            <FaBox className="me-2" /> 
                                            {selectedOrder.type?.split('_').map(word => 
                                                word.charAt(0).toUpperCase() + word.slice(1)
                                            ).join(' ') || 'Order'}
                                        </Badge>
                                    )}
                                </Col>
                            </Row>

                            {/* Order Selection */}
                            <Row className="mb-4">
                                <Col>
                                    <Card className="shadow-lg rounded-4 border-0">
                                        <Card.Body>
                                            <Card.Title className="for_title">Order History</Card.Title>
                                            {loading ? (
                                                <div className="text-center py-4">
                                                    <Spinner animation="border" />
                                                    <p>Loading orders...</p>
                                                </div>
                                            ) : error ? (
                                                <Alert variant="danger">{error}</Alert>
                                            ) : (
                                                <div className="order-list" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                    {orders.length === 0 ? (
                                                        <p className="text-muted">No orders found</p>
                                                    ) : (
                                                        <Table hover size="sm">
                                                            <thead>
                                                                <tr>
                                                                    <th>Order #</th>
                                                                    <th>Type</th>
                                                                    <th>Status</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                            {orders.map(order => (
                                                                <tr 
                                                                    key={order.id} 
                                                                    className={`cursor-pointer ${selectedOrder?.id === order.id ? 'table-primary' : ''}`}
                                                                    onClick={() => handleOrderSelect(order)}
                                                                >
                                                                    <td><strong>#{order.po_number || order.id}</strong></td>
                                                                    <td>{order.type?.split('_').map(word => 
                                                                        word.charAt(0).toUpperCase() + word.slice(1)
                                                                    ).join(' ') || 'Order'}</td>
                                                                    <td><Badge bg={getStatusColor(order.status)}>{getStatusText(order.status)}</Badge></td>
                                                                </tr>
                                                            ))}
                                                            </tbody>
                                                        </Table>
                                                    )}
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>

                            {selectedOrder && (
                                <Row>
                                    {/* LEFT COLUMN */}
                                    <Col md={6}>
                                        {/* Vendor Info (using Customer/Vendor/Driver as placeholders) */}
                                        <Card className="mb-3 shadow-lg rounded-4 border-0">
                                            <Card.Body>
                                                <Card.Title className="for_title">Order Details</Card.Title>
                                                <p className="mb-1 text-left">
                                                    <FaUserTie className="me-2 text-info"/>
                                                    <strong>Vendor:</strong> {selectedOrder.vendor?.name || 'N/A'}
                                                </p>
                                                <p className="mb-1 text-left">
                                                    <FaUser className="me-2 text-info"/>
                                                    <strong>Customer:</strong> {selectedOrder.customer?.name || 'N/A'}
                                                </p>
                                                <p className="mb-1 text-left">
                                                    <FaMapMarkerAlt className="me-2 text-info"/>
                                                    <strong>Pickup:</strong> {selectedOrder.pickup_address || 'N/A'}
                                                </p>
                                                <p className="mb-1 text-left">
                                                    <FaMapMarkerAlt className="me-2 text-info"/>
                                                    <strong>Delivery:</strong> {selectedOrder.delivery_address || 'N/A'}
                                                </p>
                                                <p className="mb-1 text-left">
                                                    <FaPhoneAlt className="me-2 text-info"/>
                                                    <strong>Driver:</strong> {selectedOrder.driver?.name || 'Unassigned'}
                                                </p>
                                                <p className="mb-1 text-left">
                                                    <strong>Amount:</strong> AED {selectedOrder.cod_amount || 0}
                                                </p>
                                                <p className="mb-1 text-left">
                                                    <strong>Remarks:</strong> {selectedOrder.notes || 'N/A'}
                                                </p>
                                            </Card.Body>
                                        </Card>

                                        {/* Timeline */}
                                        <Card className="mb-3 shadow-lg rounded-4 border-0">
                                            <Card.Body>
                                                <Card.Title className="for_title">Locations & Times</Card.Title>
                                                
                                                {trackingLoading ? (
                                                    <div className="text-center py-4">
                                                        <Spinner animation="border" size="sm" />
                                                        <p>Loading tracking data...</p>
                                                    </div>
                                                ) : trackingData && trackingData.timeline && Array.isArray(trackingData.timeline) ? (
                                                    <div className="timeline mt-4">
                                                        {trackingData.timeline.map((event, index) => (
                                                            <div key={index} className="timeline-row">
                                                                <div className="timeline-date">
                                                                    <h6 className="for_date">{formatDate(event.timestamp)}</h6>
                                                                    <small className="for_small">{formatTime(event.timestamp)}</small>
                                                                </div>
                                                                <div className={`timeline-dot ${getStatusColor(event.status)}`}></div>
                                                                <div className="timeline-status">
                                                                    <h6 className="fw-bold for_date">{getStatusText(event.status)}</h6>
                                                                    <small className="text-muted">{event.location || 'N/A'}</small>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-4">
                                                        <p className="text-muted">No detailed tracking data available</p>
                                                        <Button 
                                                           className="new-order-btn" 
                                                            size="sm"
                                                            onClick={() => fetchOrderTracking(selectedOrder.id)}
                                                        >
                                                            Refresh Tracking Data
                                                        </Button>
                                                    </div>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Col>

                                    {/* RIGHT COLUMN */}
                                    <Col md={6}>
                                        {/* Proof of Pickup */}
                                        <Card className="mb-3 shadow-lg rounded-4 border-0">
                                            <Card.Body>
                                                <Card.Title className="for_title">
                                                    <FaCheckCircle className="me-2 text-info"/> Proof of PickUp
                                                </Card.Title>
                                                {/* FIX: Checking for attachments array validity */}
                                                {trackingData && Array.isArray(trackingData.attachments) ? (
                                                    <>
                                                        {trackingData.attachments
                                                            .filter(att => att.type.toLowerCase().includes('pod') || att.type.toLowerCase().includes('pickup'))
                                                            .map((attachment, index) => (
                                                                <div key={index} className="mb-3">
                                                                    {/* Simple button to view proof in modal */}
                                                                    <Button 
                                                                        variant="outline-info" 
                                                                        size="sm" 
                                                                        className="proof_btn"
                                                                        onClick={() => handleViewProof('pod')}
                                                                    >
                                                                        <FaEye className="me-2" />View Proof {index + 1}
                                                                    </Button>
                                                                </div>
                                                            ))
                                                        }
                                                        {trackingData.attachments.filter(att => att.type.toLowerCase().includes('pod') || att.type.toLowerCase().includes('pickup')).length === 0 && (
                                                            <div className="text-center py-4">
                                                                <p className="text-muted">No pickup proof available</p>
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div className="text-center py-4">
                                                        <p className="text-muted">No proof data available</p>
                                                    </div>
                                                )}
                                            </Card.Body>
                                        </Card>

                                        {/* Proof of Delivery */}
                                        <Card className="mb-3 shadow-lg rounded-4 border-0">
                                            <Card.Body>
                                                <Card.Title className="for_title">
                                                    <FaCheckCircle className="me-2 text-success"/> Proof of Delivery
                                                </Card.Title>
                                                {/* FIX: Checking for attachments array validity */}
                                                {trackingData && Array.isArray(trackingData.attachments) ? (
                                                    <>
                                                        {trackingData.attachments
                                                            .filter(att => att.type.toLowerCase().includes('delivery'))
                                                            .map((attachment, index) => (
                                                                <div key={index} className="mb-3">
                                                                    {/* Simple button to view proof in modal */}
                                                                    <Button 
                                                                        variant="outline-success" 
                                                                        size="sm" 
                                                                        className="proof_btn"
                                                                        onClick={() => handleViewProof('delivery')}
                                                                    >
                                                                        <FaEye className="me-2" />View Proof {index + 1}
                                                                    </Button>
                                                                </div>
                                                            ))
                                                        }
                                                        {trackingData.attachments.filter(att => att.type.toLowerCase().includes('delivery')).length === 0 && (
                                                            <div className="text-center py-4">
                                                                <p className="text-muted">No delivery proof available</p>
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div className="text-center py-4">
                                                        <p className="text-muted">No proof data available</p>
                                                    </div>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            )}
                        </Container>
                    </div>
                </div>
            </div>

            {/* Proof Modal */}
            <Modal show={showProofModal} onHide={() => setShowProofModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Proof Document</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedProof ? (
                        <div className="text-center">
                            {selectedProof.mime && selectedProof.mime.startsWith('image/') ? (
                                <img 
                                    src={selectedProof.url} 
                                    alt={selectedProof.name || 'Proof Document'} 
                                    className="img-fluid"
                                />
                            ) : (
                                <iframe
                                    src={selectedProof.url}
                                    title={selectedProof.name || 'Proof Document'}
                                    width="100%"
                                    height="500px"
                                />
                            )}
                            <div className="mt-3">
                                <p><strong>Name:</strong> {selectedProof.name || 'N/A'}</p>
                                <p><strong>Type:</strong> {selectedProof.type || 'N/A'}</p>
                                <p><strong>Uploaded:</strong> {selectedProof.created_at ? 
                                    new Date(selectedProof.created_at).toLocaleString() : 'N/A'}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <p>No proof document selected</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowProofModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default SalesHistory;